import React from "react";
import ReactApexChart from "react-apexcharts";
import { Link } from "react-router-dom";
import { Col, Card, CardBody, Row } from "reactstrap";
import type { InvoiceBucketMetrics } from "../../utils/dashboardMetrics";
import { formatDashboardMoney } from "../../utils/dashboardMetrics";

interface InvestedOverviewProps {
  paidSharePercent: number;
  paid: InvoiceBucketMetrics;
  unpaid: InvoiceBucketMetrics;
  overdue: InvoiceBucketMetrics;
  currency?: string;
  loading?: boolean;
}

const InvestedOverview = ({
  paidSharePercent,
  paid,
  unpaid,
  overdue,
  currency,
  loading,
}: InvestedOverviewProps) => {
  const radialchartColors = ["#5156be", "#34c38f"];

  const options: Object = {
    chart: {
      height: 270,
      type: "radialBar",
      offsetY: -10,
    },
    plotOptions: {
      radialBar: {
        startAngle: -130,
        endAngle: 130,
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            offsetY: 10,
            fontSize: "18px",
            color: undefined,
            formatter: function (val: any) {
              return val + "%";
            },
          },
        },
      },
    },
    colors: [radialchartColors[0]],
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "horizontal",
        gradientToColors: [radialchartColors[1]],
        shadeIntensity: 0.15,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [20, 60],
      },
    },
    stroke: {
      dashArray: 4,
    },
    legend: {
      show: false,
    },
    labels: ["Paid"],
  };

  return (
    <React.Fragment>
      <Col xl={8}>
        <Card className="card-h-100">
          <CardBody>
            <div className="d-flex flex-wrap align-items-center mb-4 p-0">
              <h5 className="card-title me-2">Invoice Overview</h5>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Row className="align-items-center">
                <div className="col-sm">
                  <div id="invested-overview" className="apex-charts">
                    <ReactApexChart
                      options={options}
                      series={[paidSharePercent]}
                      type="radialBar"
                      height="263"
                      className="apex-charts"
                    />
                  </div>
                </div>
                <div className="col-sm align-self-center">
                  <div className="mt-4 mt-sm-0">
                    <p className="mb-1">Paid Invoices</p>
                    <h4>{formatDashboardMoney(paid.amount, currency)}</h4>
                    <p className="text-muted mb-4">
                      {paid.count} paid · {unpaid.count} unpaid
                      {overdue.count > 0 ? ` · ${overdue.count} overdue` : ""}
                    </p>

                    <Row className="g-0">
                      <Col xs={6}>
                        <div>
                          <p className="mb-2 text-muted text-uppercase font-size-11">
                            Paid
                          </p>
                          <h5 className="fw-medium">
                            {formatDashboardMoney(paid.amount, currency)}
                          </h5>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div>
                          <p className="mb-2 text-muted text-uppercase font-size-11">
                            Outstanding
                          </p>
                          <h5 className="fw-medium">
                            {formatDashboardMoney(unpaid.amount, currency)}
                          </h5>
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-2">
                      <Link to="/invoices-list" className="btn btn-primary btn-sm">
                        View more <i className="mdi mdi-arrow-right ms-1"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </Row>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default InvestedOverview;
